import { Link } from '@tanstack/react-router'
import { Sparkles, Wallet } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { type PaywallReason, usePaywallStore } from '@/stores/paywall-store'

const COPY: Record<
  PaywallReason,
  { title: string; body: string; icon: typeof Wallet }
> = {
  'insufficient-credits': {
    title: 'Out of GP',
    body: 'You need more Generation Points to finish this action. Top up or upgrade your plan to keep creating.',
    icon: Wallet,
  },
  'tier-required': {
    title: 'Upgrade to unlock',
    body: 'This feature is only available on a higher plan. Upgrade to access it.',
    icon: Sparkles,
  },
}

export function PaywallModal() {
  const { isOpen, reason, message, requiredAmount, close } = usePaywallStore()

  // Render nothing until we have a reason — avoids flicker.
  if (!reason) {
    return (
      <Dialog open={isOpen} onOpenChange={(next) => !next && close()}>
        <DialogContent className="sm:max-w-md" />
      </Dialog>
    )
  }

  const copy = COPY[reason]
  const Icon = copy.icon

  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="bg-primary/10 text-primary mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full">
            <Icon className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center">{copy.title}</DialogTitle>
          <DialogDescription className="text-center">
            {message ?? copy.body}
          </DialogDescription>
          {reason === 'insufficient-credits' && requiredAmount ? (
            <p className="text-muted-foreground mt-2 text-center text-xs">
              This action needs {requiredAmount} GP.
            </p>
          ) : null}
        </DialogHeader>
        <DialogFooter className="sm:justify-center sm:gap-3">
          {reason === 'insufficient-credits' ? (
            <>
              <Button asChild variant="outline" onClick={close}>
                <Link to="/top-up">Top up GP</Link>
              </Button>
              <Button asChild onClick={close}>
                <Link to="/pricing">View plans</Link>
              </Button>
            </>
          ) : (
            <Button asChild onClick={close}>
              <Link to="/pricing">See plans</Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
